import { useEffect, useRef, useState, useCallback } from 'react';
import QRCode from 'qrcode';
import { Download, Printer, Copy, Check, X, QrCode, Barcode } from 'lucide-react';

interface BarcodeGeneratorProps {
  data: string;
  label?: string;
  type?: 'qr' | 'barcode';
  size?: number;
  onClose?: () => void;
}

export default function BarcodeGenerator({ 
  data, 
  label, 
  type = 'qr', 
  size = 200,
  onClose 
}: BarcodeGeneratorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(true);

  const generateQR = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    try {
      setIsGenerating(true);
      
      if (type === 'qr') {
        await QRCode.toCanvas(canvas, data, {
          width: size,
          margin: 2,
          color: {
            dark: '#0A0A0A',
            light: '#FFFFFF',
          },
          errorCorrectionLevel: 'H',
        });
      } else {
        // Barcode-style: generate a simple barcode pattern using canvas
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        canvas.width = size;
        canvas.height = Math.floor(size * 0.6);
        
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Generate barcode pattern from data string
        const barcodeData = data.replace(/[^a-zA-Z0-9]/g, '');
        const barWidth = Math.max(2, Math.floor(canvas.width / (barcodeData.length * 2 + 4)));
        let x = barWidth * 2;
        
        ctx.fillStyle = '#0A0A0A';
        
        // Start pattern
        ctx.fillRect(x, 10, barWidth, canvas.height - 30);
        x += barWidth * 2;
        ctx.fillRect(x, 10, barWidth, canvas.height - 30);
        x += barWidth;
        
        // Data bars
        for (let i = 0; i < barcodeData.length && x < canvas.width - barWidth * 4; i++) {
          const charCode = barcodeData.charCodeAt(i);
          const pattern = charCode % 3;
          
          if (pattern === 0) {
            ctx.fillRect(x, 10, barWidth, canvas.height - 30);
            x += barWidth * 2;
          } else if (pattern === 1) {
            ctx.fillRect(x, 10, barWidth * 2, canvas.height - 30);
            x += barWidth * 3;
          } else {
            ctx.fillRect(x, 10, barWidth, canvas.height - 30);
            x += barWidth;
            ctx.fillRect(x, 10, barWidth, canvas.height - 30);
            x += barWidth * 2;
          }
        }
        
        // End pattern
        ctx.fillRect(x, 10, barWidth, canvas.height - 30);
        x += barWidth * 2;
        ctx.fillRect(x, 10, barWidth, canvas.height - 30);
        
        // Label
        ctx.fillStyle = '#0A0A0A';
        ctx.font = `${Math.max(10, size / 20)}px monospace`;
        ctx.textAlign = 'center';
        ctx.fillText(data.substring(0, 20), canvas.width / 2, canvas.height - 5);
      }
      
      setIsGenerating(false);
    } catch (err) {
      console.error('Barcode generation error:', err);
      setIsGenerating(false);
    }
  }, [data, size, type]);

  useEffect(() => {
    generateQR();
  }, [generateQR]);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const link = document.createElement('a');
    link.download = `barcode-${data.replace(/[^a-zA-Z0-9]/g, '-')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const handlePrint = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Barkod - ${data}</title>
          <style>
            body { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; margin: 0; font-family: monospace; }
            .label { margin-bottom: 10px; font-size: 14px; font-weight: bold; }
            .data { margin-top: 10px; font-size: 12px; color: #666; }
            canvas { max-width: 300px; }
          </style>
        </head>
        <body>
          ${label ? `<div class="label">${label}</div>` : ''}
          <img src="${canvas.toDataURL('image/png')}" />
          <div class="data">${data}</div>
          <script>window.onload = () => window.print();</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleCopyData = async () => {
    try {
      await navigator.clipboard.writeText(data);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = data;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {type === 'qr' ? (
            <QrCode className="w-5 h-5 text-brand-red" />
          ) : (
            <Barcode className="w-5 h-5 text-brand-red" />
          )}
          <h3 className="text-sm font-bold text-gray-900">
            {type === 'qr' ? 'QR Kod' : 'Barkod'}
          </h3>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>

      {/* Canvas */}
      <div className="flex justify-center p-4 bg-gray-50 rounded-xl">
        {isGenerating ? (
          <div className="w-[200px] h-[200px] flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <canvas ref={canvasRef} className="max-w-full" />
        )}
      </div>

      {/* Label */}
      {label && (
        <p className="text-center text-xs text-gray-500 truncate">{label}</p>
      )}

      {/* Data */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
        <span className="flex-1 text-xs font-mono text-gray-600 truncate">{data}</span>
        <button
          onClick={handleCopyData}
          className="flex-shrink-0 p-1.5 hover:bg-gray-200 rounded-md transition-colors"
          title="Kopyala"
        >
          {copied ? (
            <Check className="w-3.5 h-3.5 text-green-500" />
          ) : (
            <Copy className="w-3.5 h-3.5 text-gray-400" />
          )}
        </button>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={handleDownload}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-brand-red hover:bg-red-700 text-white text-sm font-medium rounded-xl transition-colors"
        >
          <Download className="w-4 h-4" />
          İndir
        </button>
        <button
          onClick={handlePrint}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-xl transition-colors"
        >
          <Printer className="w-4 h-4" />
          Yazdır
        </button>
      </div>
    </div>
  );
}
